import { dlog } from './debug';
/**
 * Group matches by date, handling shared group labels and edge cases
 * 
 * If a viewer has a shared group with custom label (e.g., "Tournament Day 1")
 * and creates their own match on the same date, they should see:
 * - "Tournament Day 1" group (shared matches)
 * - "Today" group (their own match)
 * 
 * @param {Array} matches - Array of match objects
 * @param {Object} sharedGroupLabels - { dateKey: label } mapping for shared group labels
 * @param {Object} customLabels - { dateKey: label } mapping for user's own custom labels
 * @returns {Array} - Array of { dateKey, date, label, matches, isSharedGroup } objects
 */

import { formatGroupLabel, getDateKey } from './matchGrouping';

export function groupMatchesWithSharedLabels(matches, sharedGroupLabels = {}, customLabels = {}) {
  if (!matches || matches.length === 0) {
    return [];
  }
  
  // Separate matches by source: shared groups vs own matches
  // Group shared matches by fromSharedGroup ID first, then by dateKey
  const sharedMatchesByGroup = new Map(); // fromSharedGroup -> matches
  const sharedMatchesByDateKey = new Map(); // dateKey -> matches (for matches without fromSharedGroup)
  const ownMatches = new Map(); // dateKey -> viewer's own matches
  
  matches.forEach(match => {
    const timestamp = match.createdAt || Date.now();
    const dateKey = getDateKey(timestamp);
    
    // Check if match came from a shared group
    // Also check if this dateKey has a shared group label (for backward compatibility)
    const hasSharedLabel = sharedGroupLabels[dateKey];
    const isFromSharedGroup = match.fromSharedGroup || (hasSharedLabel && !match.isOwner);
    
    if (isFromSharedGroup) {
      // If match has a fromSharedGroup ID, group by that ID
      // This ensures collaborative matches stay with their original shared group
      if (match.fromSharedGroup) {
        if (!sharedMatchesByGroup.has(match.fromSharedGroup)) {
          sharedMatchesByGroup.set(match.fromSharedGroup, []);
        }
        sharedMatchesByGroup.get(match.fromSharedGroup).push(match);
        dlog(`📦 Added match ${match.id} to shared group ${match.fromSharedGroup}`, {
          fromSharedGroup: match.fromSharedGroup,
          sharedGroupDateKey: match.sharedGroupDateKey,
          dateKey: dateKey,
          isOwner: match.isOwner
        });
      } else {
        // Fallback: group by dateKey for matches without fromSharedGroup ID
        if (!sharedMatchesByDateKey.has(dateKey)) {
          sharedMatchesByDateKey.set(dateKey, []);
        }
        sharedMatchesByDateKey.get(dateKey).push(match);
      }
    } else {
      // Viewer's own match (created or viewed individually)
      if (!ownMatches.has(dateKey)) {
        ownMatches.set(dateKey, []);
      }
      ownMatches.get(dateKey).push(match);
    }
  });
  
  // Build groups
  const groups = [];
  const processedDateKeys = new Set();
  
  // First, add shared groups grouped by fromSharedGroup ID
  sharedMatchesByGroup.forEach((sharedMatchesList, groupId) => {
    // Use the shared group's dateKey if stored on the match, otherwise derive from createdAt
    // All matches in the same shared group should have the same sharedGroupDateKey
    // Find the first match that has sharedGroupDateKey, or use the first match's dateKey
    let dateKey = null;
    let timestamp = Date.now();
    
    // Try to find sharedGroupDateKey from any match in the group
    for (const match of sharedMatchesList) {
      if (match.sharedGroupDateKey) {
        dateKey = match.sharedGroupDateKey;
        timestamp = match.createdAt || Date.now();
        break;
      }
    }
    
    // If no match has sharedGroupDateKey, try to find it from sharedGroupLabels
    // Look for any dateKey in sharedGroupLabels that might match this group
    if (!dateKey) {
      // First, try to derive from the first match's createdAt
      const firstMatch = sharedMatchesList[0];
      timestamp = firstMatch.createdAt || Date.now();
      const derivedDateKey = getDateKey(timestamp);
      
      // Check if this dateKey has a shared group label (which means it's the shared group's dateKey)
      if (sharedGroupLabels[derivedDateKey]) {
        dateKey = derivedDateKey;
      } else {
        // Try to find a dateKey in sharedGroupLabels that matches any match's dateKey
        // This handles the case where matches might have slightly different timestamps
        for (const match of sharedMatchesList) {
          const matchDateKey = getDateKey(match.createdAt || Date.now());
          if (sharedGroupLabels[matchDateKey]) {
            dateKey = matchDateKey;
            timestamp = match.createdAt || Date.now();
            break;
          }
        }
        
        // If still no dateKey found, use the derived one
        if (!dateKey) {
          dateKey = derivedDateKey;
        }
      }
    }
    
    const date = new Date(timestamp);
    
    // Use the shared group label if available, otherwise format the date
    const sharedLabel = sharedGroupLabels[dateKey] || formatGroupLabel(date);
    
    dlog(`🔍 Grouping shared group ${groupId}:`, {
      dateKey,
      label: sharedLabel,
      matchCount: sharedMatchesList.length,
      sharedGroupLabels: Object.keys(sharedGroupLabels),
      matches: sharedMatchesList.map(m => ({ 
        id: m.id, 
        sharedGroupDateKey: m.sharedGroupDateKey, 
        fromSharedGroup: m.fromSharedGroup,
        isOwner: m.isOwner,
        createdAt: m.createdAt
      }))
    });
    
    // Sort matches within group by original creation time (newest first)
    const sortedMatches = sharedMatchesList.sort((a, b) => {
      const aTime = a.originalCreatedAt || a.createdAt || 0;
      const bTime = b.originalCreatedAt || b.createdAt || 0;
      return bTime - aTime; // Newest first
    });
    
    groups.push({
      dateKey: `${dateKey}_shared_${groupId}`, // Include groupId to ensure uniqueness
      originalDateKey: dateKey,
      date,
      label: sharedLabel,
      matches: sortedMatches,
      isSharedGroup: true,
      sharedGroupId: groupId
    });
    
    processedDateKeys.add(dateKey);
  });
  
  // Then add shared groups grouped by dateKey (for backward compatibility)
  sharedMatchesByDateKey.forEach((sharedMatchesList, dateKey) => {
    const date = new Date(sharedMatchesList[0].createdAt || Date.now());
    const sharedLabel = sharedGroupLabels[dateKey] || formatGroupLabel(date);
    
    // Sort matches within group by original creation time (newest first)
    const sortedSharedMatches = sharedMatchesList.sort((a, b) => {
      const aTime = a.originalCreatedAt || a.createdAt || 0;
      const bTime = b.originalCreatedAt || b.createdAt || 0;
      return bTime - aTime; // Newest first
    });
    
    groups.push({
      dateKey: `${dateKey}_shared`,
      originalDateKey: dateKey,
      date,
      label: sharedLabel,
      matches: sortedSharedMatches,
      isSharedGroup: true
    });
    
    processedDateKeys.add(dateKey);
  });
  
  // Then, add viewer's own matches
  ownMatches.forEach((ownMatchesList, dateKey) => {
    const date = new Date(ownMatchesList[0].createdAt || Date.now());
    const label = customLabels[dateKey] || formatGroupLabel(date);
    
    // If we already have a shared group for this date, use a different dateKey
    const groupDateKey = processedDateKeys.has(dateKey) 
      ? `${dateKey}_own` 
      : dateKey;
    
    // Sort matches within group by original creation time (newest first)
    const sortedOwnMatches = ownMatchesList.sort((a, b) => {
      const aTime = a.originalCreatedAt || a.createdAt || 0;
      const bTime = b.originalCreatedAt || b.createdAt || 0;
      return bTime - aTime; // Newest first
    });
    
    groups.push({
      dateKey: groupDateKey,
      originalDateKey: dateKey,
      date,
      label,
      matches: sortedOwnMatches,
      isSharedGroup: false
    });
  });
  
  // Sort by date (newest first)
  groups.sort((a, b) => {
    // If same original date, shared groups come first
    if (a.originalDateKey === b.originalDateKey) {
      return a.isSharedGroup ? -1 : 1;
    }
    return b.date.getTime() - a.date.getTime();
  });
  
  return groups;
}

